import {
   Box,
   Toolbar,
   Typography,
   Button,
   Badge,
   Chip,
   ButtonBase,
   Menu,
   MenuItem,
   Divider,
   ListItemIcon,
   ListItemText,
   Tooltip,
   useTheme,
} from '@mui/material';
import {useState} from 'react';
import {NavLink, useLocation} from 'react-router';
import {useAtomValue, useSetAtom} from 'jotai';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import SyncIcon from '@mui/icons-material/Sync';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {authStatusAtom, cachedSessionAtom, isOnlineAtom, signOutAtom} from '@/state/authAtoms';
import {offlineQueueAtom, syncStateAtom} from '@/state/offlineQueue';
import {themeModePreferenceAtom, type ThemeModePreference} from '@/state/themeAtom';
import {useDevice} from '@/hooks/useDevice.ts';
import {routerLinks} from '@/Constants.ts';
import {RoleType} from '@/types/types.ts';
import {getInitials} from '@/lib/util.ts';
import {needsReviewCountAtom} from '@/state/runTicketAtoms';

// const NAV_ITEMS = [
//    {label: 'Dashboard', path: '/dashboard'},
//    {label: 'Wells', path: routerLinks.wells},
//    {label: 'Barrel Tests', path: '/barrel-tests'},
//    {label: 'Reports', path: '/reports'},
// ];

const NAV_ITEMS = [
   {label: 'Wells', path: routerLinks.wells},
   {label: 'Barrel Tests', path: routerLinks.barrelTests},
   {label: 'Run Tickets', path: routerLinks.runTickets},
   {label: 'Pulled Bottoms', path: routerLinks.pulledBottoms},
];

// Split around the Reports dropdown (rendered separately, right after
// these) rather than one flat admin list — same grouping as TopNav.tsx's
// ADMIN_NAV_ITEMS_MAIN/REPORTS_MENU_ITEMS, see the comment there.
const ADMIN_NAV_ITEMS_MAIN = [
   {label: 'Dashboard', path: routerLinks.dashboard},
   {label: 'Wells', path: routerLinks.wells},
   {label: 'Barrel Tests', path: routerLinks.barrelTests},
   {label: 'Run Tickets', path: routerLinks.runTickets},
   {label: 'Pulled Bottoms', path: routerLinks.pulledBottoms},
];

const REPORTS_MENU_ITEMS = [
   {label: 'Reports', path: routerLinks.reports},
   {label: 'Audit Report', path: routerLinks.auditReport},
   {label: 'Tanks Allocations Report', path: routerLinks.tanksAllocationsReport},
   {label: 'Production Report', path: routerLinks.productionReport},
   {label: 'OGP03I Export', path: routerLinks.ogp03iExport},
];

// FIXED 2026-08-27 (Duane): an Admin/SuperAdmin who ends up on a mobile
// device (not this app's primary supported path, but "it would be good
// to be usable") had no way to reach any Setup page at all — BottomNav
// never had a Setup dropdown to begin with, unlike TopNav.tsx (see its
// identical SETUP_MENU_ITEMS). Same four items/paths as there.
const SETUP_MENU_ITEMS = [
   {label: 'Users', path: routerLinks.userListSetup},
   {label: 'Wells Setup', path: routerLinks.wellListSetup},
   {label: 'Leases Setup', path: routerLinks.leaseListSetup},
   // Price Config removed per Duane, 2026-08-28 — see TopNav.tsx's
   // identical SETUP_MENU_ITEMS comment.
   // {label: 'Price Config', path: routerLinks.priceConfigSetup},
];

const THEME_OPTIONS: {value: ThemeModePreference; label: string; icon: JSX.Element}[] = [
   {value: 'light', label: 'Light', icon: <LightModeIcon fontSize='small' />},
   {value: 'dark', label: 'Dark', icon: <DarkModeIcon fontSize='small' />},
   {value: 'system', label: 'System', icon: <SettingsBrightnessIcon fontSize='small' />},
];

export function BottomNav() {
   const theme = useTheme();
   const status = useAtomValue(authStatusAtom);
   const isOnline = useAtomValue(isOnlineAtom);
   const cachedSession = useAtomValue(cachedSessionAtom);
   const queue = useAtomValue(offlineQueueAtom);
   const syncState = useAtomValue(syncStateAtom);
   const needsReviewCount = useAtomValue(needsReviewCountAtom);
   const signOut = useSetAtom(signOutAtom);
   const themePreference = useAtomValue(themeModePreferenceAtom);
   const setThemePreference = useSetAtom(themeModePreferenceAtom);
   const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
   const [reportsMenuAnchor, setReportsMenuAnchor] = useState<null | HTMLElement>(null);
   const [setupMenuAnchor, setSetupMenuAnchor] = useState<null | HTMLElement>(null);
   const device = useDevice({mobileBreakpoint: 1070});
   const location = useLocation();

   const firstName = cachedSession?.firstName?.trim();
   const lastName = cachedSession?.lastName?.trim();
   const fullName = [firstName, lastName].filter(Boolean).join(' ');
   const displayName = fullName || cachedSession?.username || 'Inactive user';

   const isAdmin = cachedSession?.role === RoleType.Admin || cachedSession?.role === RoleType.SuperAdmin;
   const navItems = isAdmin ? ADMIN_NAV_ITEMS_MAIN : NAV_ITEMS;
   const isReportsActive = REPORTS_MENU_ITEMS.some((item) => location.pathname === item.path);
   const isSetupActive = SETUP_MENU_ITEMS.some((item) => location.pathname === item.path);

   const initials = getInitials(cachedSession);

   // FIXED 2026-08-28 (Duane): "While I'm offline the sync icon is
   // turning even though I am offline — it should be showing a number
   // and not the syncing icon." `syncStateAtom` is driven by
   // useOfflineSync.ts's own retry effect, which gates on the SAME
   // `isOnlineAtom` this component's "Offline" Chip reads — so in
   // theory the two should never disagree. In practice they can: per
   // useAuthBootstrap.ts's own comment, `navigator.onLine`/the
   // 'online'/'offline' events only reflect whether a network
   // INTERFACE is up, not whether it actually has real internet
   // reachability (the classic "connected to Wi-Fi with no real
   // signal" case) — exactly the gap the OS-level "No internet
   // connection" banner in Duane's screenshot is built to catch, which
   // this in-page atom has no way to see. When that happens,
   // `isOnlineAtom` can read stale-true while the device is genuinely
   // offline, so useOfflineSync's effect still fires, flips
   // `syncStateAtom` to 'syncing', and hangs out the full
   // MUTATION_SYNC_TIMEOUT_MS against a request that was never really
   // going anywhere — repeating every SYNC_POLL_INTERVAL_MS poll tick
   // for as long as the interface keeps looking connected. Rather than
   // trying to make `isOnlineAtom` itself perfectly reflect real
   // reachability (a much bigger change), this just makes the visible
   // spinner trust the same "Offline" Chip's own condition: never claim
   // to be actively syncing on a render where this component is ALSO
   // telling the user they're offline — show the queued-count number
   // instead, same as the plain 'idle' case. The real background retry
   // still runs unchanged; only what's DISPLAYED here is gated.
   const isSyncing = syncState === 'syncing' && isOnline;

   const renderNavButton = (item: {label: string; path: string}) => {
      const button = (
         <Button
            key={item.path}
            size={device.isSmallViewport ? 'small' : 'medium'}
            component={NavLink}
            to={item.path}
            sx={{
               color: 'text.secondary',
               px: {xs: 0.5, sm: 1, md: 2},
               '&.active': {
                  bgcolor: 'rgba(225,90,44,0.1)',
                  color: 'primary.main',
               },
            }}
         >
            {item.label}
         </Button>
      );
      // Durable "N tickets awaiting review" indicator — see
      // needsReviewCountAtom (state/runTicketAtoms.ts). Only Run Tickets
      // carries a badge; no other nav item has a count worth surfacing
      // this way.
      if (item.path === routerLinks.runTickets && needsReviewCount > 0) {
         return (
            <Badge
               key={item.path}
               badgeContent={needsReviewCount}
               color='success'
               overlap='rectangular'
               // Same fix as TopNav.tsx's identical badge — see its comment.
               sx={{
                  '& .MuiBadge-badge': {
                     top: {xs: 4, sm: 5, md: 6},
                     right: {xs: 6, sm: 10, md: 16},
                  },
               }}
            >
               {button}
            </Badge>
         );
      }
      return button;
   };

   return (
      <Toolbar
         sx={{
            gap: 0.5,
            justifyContent: 'space-between',
            minHeight: 60,
            px: 0.5,
            borderTop: `1px solid ${theme.palette.divider}`,
         }}
      >
         {/* FIXED 2026-08-27 (Duane): an Admin/SuperAdmin's nav set here
             (4 buttons + Reports + now Setup) doesn't reliably fit next
             to the chips/date/account cluster on a real phone width —
             unlike TopNav, this bar has no 2-row fallback (it's fixed to
             the bottom of the viewport; growing it would permanently
             eat screen space for every mobile user, including the much
             more common field-role case that only ever has 3 items and
             never needs it). Scoping the fix to just this box instead:
             it scrolls horizontally on overflow rather than clipping/
             pushing the account menu off-screen, and flexShrink: 0 on
             each button keeps them from being squeezed unreadably thin
             first. The chips/date/account cluster below keeps its
             default flex-shrink instead, so it's never the side that
             gives up space. */}
         <Box
            sx={{
               display: 'flex',
               gap: 0.5,
               alignItems: 'center',
               overflowX: 'auto',
               WebkitOverflowScrolling: 'touch',
               flexShrink: 1,
               minWidth: 0,
               // FIXED 2026-08-28 (Duane): "The Run Tickets badge is
               // clipped on the top and the right side." Setting
               // overflowX here forces the browser to also compute
               // overflow-y as non-visible (per the CSS spec, a
               // non-'visible' overflow-x implies the same for
               // overflow-y even when it isn't set explicitly) — so this
               // box clips in BOTH directions, not just horizontally as
               // intended. The needs-review Badge below deliberately
               // positions its dot to poke slightly outside its own
               // button's box (that's the whole point of a corner
               // badge), which put it right in the clipped zone. Padding
               // the box gives that overhang room to render within the
               // box's own bounds — inside the padding, not outside the
               // content it clips — without changing the intended
               // horizontal-scroll behavior.
               pt: 1,
               pr: 1,
               '& > *': {flexShrink: 0},
            }}
         >
            {navItems.map(renderNavButton)}
            {isAdmin && (
               <>
                  <Button
                     size={device.isSmallViewport ? 'small' : 'medium'}
                     onClick={(e) => setReportsMenuAnchor(e.currentTarget)}
                     endIcon={<KeyboardArrowDownIcon fontSize='small' />}
                     sx={{
                        color: 'text.secondary',
                        px: {xs: 0.5, sm: 1, md: 2},
                        ...(isReportsActive && {bgcolor: 'rgba(225,90,44,0.1)', color: 'primary.main'}),
                     }}
                  >
                     Reports
                  </Button>
                  <Menu
                     anchorEl={reportsMenuAnchor}
                     open={Boolean(reportsMenuAnchor)}
                     onClose={() => setReportsMenuAnchor(null)}
                  >
                     {REPORTS_MENU_ITEMS.map((item) => (
                        <MenuItem
                           key={item.path}
                           component={NavLink}
                           to={item.path}
                           selected={location.pathname === item.path}
                           onClick={() => setReportsMenuAnchor(null)}
                        >
                           {item.label}
                        </MenuItem>
                     ))}
                  </Menu>
                  <Button
                     size={device.isSmallViewport ? 'small' : 'medium'}
                     onClick={(e) => setSetupMenuAnchor(e.currentTarget)}
                     endIcon={<KeyboardArrowDownIcon fontSize='small' />}
                     sx={{
                        color: 'text.secondary',
                        px: {xs: 0.5, sm: 1, md: 2},
                        ...(isSetupActive && {bgcolor: 'rgba(225,90,44,0.1)', color: 'primary.main'}),
                     }}
                  >
                     Setup
                  </Button>
                  <Menu
                     anchorEl={setupMenuAnchor}
                     open={Boolean(setupMenuAnchor)}
                     onClose={() => setSetupMenuAnchor(null)}
                  >
                     {SETUP_MENU_ITEMS.map((item) => (
                        <MenuItem
                           key={item.path}
                           component={NavLink}
                           to={item.path}
                           selected={location.pathname === item.path}
                           onClick={() => setSetupMenuAnchor(null)}
                        >
                           {item.label}
                        </MenuItem>
                     ))}
                  </Menu>
               </>
            )}
         </Box>
         {/* FIXED 2026-08-27 (Duane): "it showed 1 pending over the top
             of the account menu. I can't log out." — bounded/ellipsized
             this chip so it couldn't visually spill onto the
             account-menu button. FIXED AGAIN, DIFFERENTLY, 2026-08-28
             (Duane): even bounded, this chip could still fully cover the
             Run Tickets nav button on a narrow admin phone width — "that
             notification needs to move to a better place." The pending-
             sync chip itself is gone from this row now; it's a small
             Badge on the account avatar instead (see below), which
             costs no row width at all rather than needing ever-tighter
             bounding.
             FIXED AGAIN, 2026-08-28 (Duane, screenshot from a field
             User's real phone while offline): "too much space left of
             the offline chip / too much space left of the account menu /
             offline chip cut off." Two real bugs here, not one:
             (1) the flexGrow spacer Box that used to sit right before
             this comment was pure dead weight — with only 3 nav items on
             a field User's phone (no Reports/Setup) there's rarely
             positive free space for it to actually grow into, so it just
             sat at its 8px minWidth, ADDING a second 8px+8px gap on top
             of the Toolbar's own `gap: 1` that already separates every
             pair of children here — visible as an oversized dead zone
             between the nav cluster and this one. Removed entirely; the
             Toolbar's own gap now spaces this box the same consistent
             8px it already uses between this box and the account button
             next to it. (2) This box's `flexShrink: 1`/`overflow: hidden`
             contradicted its OWN neighbor's stated behavior — the
             account-menu Box just below is `flexShrink: 0` specifically
             so "it's never the side that gives up space" (see that box's
             comment) — but this one, unlike the account box, WAS
             shrinking, and since the Chip itself doesn't reflow (it's
             one atomic pill, not text that can wrap), shrinking its
             wrapper below the Chip's own rendered width doesn't ellipsize
             the label gracefully — it hard-clips the whole Chip at the
             box edge ("Offli|"). Changed to `flexShrink: 0` to match the
             account box's precedent: the nav-items Box to the left
             already has an accepted, working overflow affordance
             (horizontal scroll, see its own 2026-08-27 comment) — that's
             the one part of this row that SHOULD give up space first, not
             this one or the account menu. */}
         <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0}}>
            {/* ADDED 2026-08-28 (Duane, per the App-Development Plan's
                offline requirement: "If no network connection detected,
                display offline mode indicator. Data entered offline queues
                for sync.") — same reasoning as TopNav.tsx's identical pair
                of Chips: this one tracks live isOnline (the general "no
                network connection detected" signal the requirement asks
                for), the other only reflects the auth-bootstrap status,
                set once at startup and never re-evaluated during the
                session. Mutually exclusive with the one below so only one
                ever shows in this bounded box at a time. */}
            {!isOnline && (
               <Chip
                  icon={<WifiOffIcon />}
                  label='Offline'
                  color='warning'
                  size='small'
                  sx={{
                     maxWidth: {xs: 140, sm: 220},
                     '& .MuiChip-label': {overflow: 'hidden', textOverflow: 'ellipsis'},
                  }}
               />
            )}
            {/* Only shown once back online — see the comment above. */}
            {status === 'offline-cached' && isOnline && (
               <Chip
                  icon={<WifiOffIcon />}
                  label='Offline — cached session'
                  color='warning'
                  size='small'
                  sx={{
                     maxWidth: {xs: 140, sm: 220},
                     '& .MuiChip-label': {overflow: 'hidden', textOverflow: 'ellipsis'},
                  }}
               />
            )}
         </Box>
         {/* FIXED 2026-08-28 (Duane): this row used to also carry a
             disabled ResponsiveDatePicker showing today's date, right
             next to the account-menu button. Per Duane, the date has no
             business being here on mobile at all (WellsListPage's own
             sticky top toolbar shows it now instead — see the "FIXED
             2026-08-28" comment there); it was also eating into the
             width this already-crowded row needed for the sync-status
             chips and nav buttons on the left (see the overflow/
             ellipsis comments above and on the nav-items Box below) —
             removing it gives that scarce space back on a narrow phone
             screen. Desktop's TopNav keeps its own copy unchanged. */}
         <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0}}>
            <ButtonBase
               onClick={(e) => setMenuAnchor(e.currentTarget)}
               sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  borderRadius: 2,
                  px: 0.5,
                  py: 0.5,
                  '&:hover': {bgcolor: 'action.hover'},
               }}
            >
               {/* 2026-08-28 (Duane): the pending-sync indicator moved
                   here from a standalone Chip in the row to its left
                   (see the FIXED 2026-08-28 comment above) — a Badge
                   overlaid on the avatar costs no row width, so it can
                   never again cover a nav button no matter how crowded
                   this bar gets. Shows a spinning sync icon while
                   actively syncing, the raw pending count otherwise;
                   color flips to error if the last sync attempt failed
                   (queue is non-empty but nothing's in flight). Wrapped
                   in a Tooltip since a bare number badge alone doesn't
                   carry the "syncing" vs "pending" vs "error" distinction
                   the old chip's label text did. */}
               <Tooltip
                  title={
                     queue.length === 0
                        ? ''
                        : isSyncing
                          ? `Syncing ${queue.length} pending change${queue.length === 1 ? '' : 's'}…`
                          : syncState === 'error'
                            ? `${queue.length} change${queue.length === 1 ? '' : 's'} couldn't sync — will retry automatically`
                            : `${queue.length} change${queue.length === 1 ? '' : 's'} waiting to sync`
                  }
               >
                  <Badge
                     badgeContent={
                        isSyncing ? (
                           <SyncIcon sx={{fontSize: 12, animation: 'spin 1.2s linear infinite'}} />
                        ) : (
                           queue.length
                        )
                     }
                     invisible={queue.length === 0}
                     color={syncState === 'error' ? 'error' : 'warning'}
                     overlap='circular'
                     anchorOrigin={{vertical: 'top', horizontal: 'right'}}
                     sx={{
                        '& .MuiBadge-badge': {
                           minWidth: 16,
                           height: 16,
                           padding: 0,
                           fontSize: 10,
                           '@keyframes spin': {from: {transform: 'rotate(0deg)'}, to: {transform: 'rotate(360deg)'}},
                        },
                     }}
                  >
                     <Box
                        sx={{
                           width: 28,
                           height: 28,
                           flexShrink: 0,
                           borderRadius: '50%',
                           bgcolor: 'secondary.main',
                           color: 'secondary.contrastText',
                           display: 'flex',
                           alignItems: 'center',
                           justifyContent: 'center',
                           fontSize: 12,
                           fontWeight: 700,
                        }}
                     >
                        {initials || <AccountCircleIcon fontSize='small' />}
                     </Box>
                  </Badge>
               </Tooltip>
               <Typography
                  variant='body2'
                  sx={{fontWeight: 600, display: {xs: 'none', md: 'block'}, whiteSpace: 'nowrap'}}
               >
                  {displayName}
               </Typography>
               <KeyboardArrowDownIcon fontSize='small' sx={{color: 'text.secondary'}} />
            </ButtonBase>
         </Box>
         <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
            {THEME_OPTIONS.map((opt) => (
               <MenuItem
                  key={opt.value}
                  selected={themePreference === opt.value}
                  onClick={() => setThemePreference(opt.value)}
               >
                  <ListItemIcon>{opt.icon}</ListItemIcon>
                  <ListItemText>{opt.label}</ListItemText>
                  {themePreference === opt.value && <CheckIcon fontSize='small' sx={{ml: 2, color: 'primary.main'}} />}
               </MenuItem>
            ))}

            <Divider />

            <MenuItem
               onClick={() => {
                  setMenuAnchor(null);
                  void signOut();
               }}
            >
               Sign out
            </MenuItem>
         </Menu>
      </Toolbar>
   );
}
